import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      toast.success(data.message);
      navigate("/auth");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-pb-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-pb-surface border border-pb-border p-8" data-testid="reset-password-page">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-5 h-5 text-pb-red" />
          <span className="font-display font-black text-xl uppercase">Set a New Password</span>
        </div>
        <p className="text-sm text-zinc-500 mb-6">Choose a new password for your Pickle Bullpen account.</p>
        {!token ? (
          <p className="text-sm text-red-400" data-testid="reset-no-token">This reset link is missing its token — open the link from your email again, or request a new one from the <Link to="/auth" className="underline text-pb-red">login page</Link>.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">New Password</label>
              <input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters" required minLength={8}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Confirm Password</label>
              <input data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                placeholder="Repeat it" required minLength={8}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
            </div>
            <button type="submit" data-testid="reset-password-submit" disabled={busy}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
